import type { Comment, CommentListResponse, CommentReactionChip } from '@tomato/shared';
import { COMMENT_LIMITS, COMMENT_REACTIONS } from '@tomato/shared';
import { useEffect, useState } from 'react';

import { api, ApiError } from '../api/client';
import { formatWhen } from '../lib/markdown';
import { useSessionStore } from '../store/session';
import { Button, Notice, Spinner, TextArea } from './ui';

/**
 * 반응 스티커 줄 — 붙어 있는 이모지 칩들 + 피커(+) 버튼.
 * 스티커 느낌: 칩마다 살짝 기울어져 있고(이모지별 고정 각도), 붙을 때 팡 튄다.
 * 카운트만 보인다 — 누가 눌렀는지는 서버가 아예 내려주지 않는다.
 */
function ReactionStickers({
  comment,
  onChange,
}: {
  comment: Comment;
  onChange: (id: string, reactions: CommentReactionChip[]) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  async function toggle(emoji: string) {
    if (busy) return;
    setBusy(true);
    setPickerOpen(false);
    try {
      const res = await api.post<{ reactions: CommentReactionChip[] }>(
        `/api/comments/${comment.id}/reactions`,
        { emoji },
      );
      onChange(comment.id, res.reactions);
    } catch {
      /* 실패는 조용히 — 다음 로드 때 맞춰진다. */
    } finally {
      setBusy(false);
    }
  }

  /** 이모지별 고정 기울기(-4°~4°) — 아무렇게나 붙인 스티커 느낌, 리렌더에도 흔들리지 않게 결정론. */
  function tilt(emoji: string): number {
    const code = emoji.codePointAt(0) ?? 0;
    return ((code % 9) - 4) * 1.0;
  }

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
      {comment.reactions.map((r) => (
        <button
          key={r.emoji}
          type="button"
          disabled={busy}
          onClick={() => void toggle(r.emoji)}
          aria-label={`${r.emoji} 반응 ${r.count}개${r.mine ? ' (내가 누름)' : ''}`}
          style={{ rotate: `${tilt(r.emoji)}deg` }}
          className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[13px] shadow-sm transition active:scale-90 ${
            r.mine
              ? 'bg-tomato-50 text-tomato-700 hairline-accent'
              : 'bg-surface text-ink-600 hairline hover:bg-ink-50'
          }`}
        >
          {/* count가 바뀔 때마다 리마운트 → 스티커가 팡 튄다. */}
          <span key={r.count} className="feedback-pop text-[14px] leading-none">
            {r.emoji}
          </span>
          {r.count > 1 && <span className="tabular-nums text-[11px] font-medium">{r.count}</span>}
        </button>
      ))}

      <div className="relative">
        <button
          type="button"
          aria-label="반응 추가"
          onClick={() => setPickerOpen((v) => !v)}
          className={`flex size-6 items-center justify-center rounded-full text-[13px] transition ${
            pickerOpen ? 'bg-ink-100 text-ink-700' : 'text-ink-300 hover:bg-ink-100 hover:text-ink-600'
          }`}
        >
          {comment.reactions.length === 0 ? '😊+' : '+'}
        </button>
        {pickerOpen && (
          <div className="absolute bottom-full left-0 z-10 mb-1.5 flex gap-0.5 rounded-full bg-surface px-2 py-1.5 shadow-raised">
            {COMMENT_REACTIONS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => void toggle(emoji)}
                aria-label={`${emoji} 반응 남기기`}
                className="flex size-8 items-center justify-center rounded-full text-[18px] transition hover:bg-ink-100 active:scale-90"
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CommentBody({ comment }: { comment: Comment }) {
  if (comment.deleted) {
    return <p className="text-[14px] text-ink-300">삭제된 댓글이에요</p>;
  }
  // 댓글은 마크다운을 렌더링하지 않는다. 줄바꿈만 살린다 —
  // 짧은 대화에 서식이 필요하지 않고, 렌더링 표면을 늘릴 이유도 없다.
  return (
    <p className="text-[14px] leading-relaxed whitespace-pre-wrap text-ink-700">
      {comment.bodyMd}
    </p>
  );
}

function CommentItem({
  comment,
  isReply,
  onReply,
  onDelete,
  onReactionChange,
}: {
  comment: Comment;
  isReply: boolean;
  onReply: (id: string) => void;
  onDelete: (id: string) => void;
  onReactionChange: (id: string, reactions: CommentReactionChip[]) => void;
}) {
  const [confirming, setConfirming] = useState(false);

  return (
    <div className={isReply ? 'border-l-2 border-ink-100 pl-4' : ''}>
      {/* 삭제된 댓글은 작성자·시각도 드러내지 않는다. 자리만 남기는 게 목적이다. */}
      {!comment.deleted && (
        <div className="flex items-baseline gap-1.5 text-[12px] text-ink-400">
          <span className="font-medium text-ink-700">{comment.author?.nickname}</span>
          <span aria-hidden>·</span>
          <span>{formatWhen(comment.createdAt)}</span>
        </div>
      )}

      <div className={comment.deleted ? '' : 'mt-1'}>
        <CommentBody comment={comment} />
      </div>

      {!comment.deleted && <ReactionStickers comment={comment} onChange={onReactionChange} />}

      {!comment.deleted && (
        <div className="mt-1 flex gap-2 text-[12px]">
          {!isReply && (
            <button
              onClick={() => onReply(comment.id)}
              className="text-ink-400 transition hover:text-ink-700"
            >
              답글
            </button>
          )}
          {comment.canDelete &&
            (confirming ? (
              <>
                <button
                  onClick={() => setConfirming(false)}
                  className="text-ink-400 transition hover:text-ink-700"
                >
                  취소
                </button>
                <button
                  onClick={() => onDelete(comment.id)}
                  className="font-medium text-tomato-600"
                >
                  정말 삭제할까요?
                </button>
              </>
            ) : (
              <button
                onClick={() => setConfirming(true)}
                className="text-ink-400 transition hover:text-tomato-600"
              >
                삭제
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

export function CommentSection({ postId, onCountChange }: { postId: string; onCountChange?: () => void }) {
  const [items, setItems] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [asAdmin, setAsAdmin] = useState(false);
  const refreshMe = useSessionStore((s) => s.refreshMe);
  const isAdmin = useSessionStore((s) => s.me?.role === 'admin');

  async function load() {
    const res = await api.get<CommentListResponse>(`/api/posts/${postId}/comments`);
    setItems(res.items);
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void api
      .get<CommentListResponse>(`/api/posts/${postId}/comments`)
      .then((res) => !cancelled && setItems(res.items))
      .catch(() => !cancelled && setError('댓글을 불러오지 못했어요.'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [postId]);

  async function submit() {
    if (!body.trim() || busy) return;
    setBusy(true);
    setError('');
    try {
      await api.post(`/api/posts/${postId}/comments`, {
        bodyMd: body.trim(),
        parentId: replyTo,
        ...(isAdmin && asAdmin ? { asAdmin: true } : {}),
      });
      setBody('');
      setReplyTo(null);
      await load();
      onCountChange?.();
      void refreshMe(); // 댓글 10토마토 — 잔액 바로 반영

    } catch (err) {
      setError(err instanceof ApiError ? err.message : '남기지 못했어요.');
    } finally {
      setBusy(false);
    }
  }

  /** 토글 응답의 칩만 그 자리에서 갈아끼운다 — 전체 목록 재조회 없이. */
  function updateReactions(id: string, reactions: CommentReactionChip[]) {
    setItems((prev) => prev.map((c) => (c.id === id ? { ...c, reactions } : c)));
  }

  async function remove(id: string) {
    try {
      await api.del(`/api/comments/${id}`);
      await load();
      onCountChange?.();
    } catch {
      setError('삭제하지 못했어요.');
    }
  }

  // parentId로 묶어 1단계 트리를 만든다.
  const roots = items.filter((c) => c.parentId === null);
  const repliesOf = (id: string) => items.filter((c) => c.parentId === id);
  const liveCount = items.filter((c) => !c.deleted).length;

  return (
    <section className="mt-8">
      <h2 className="mb-3 text-[15px] font-semibold text-ink-900">
        댓글 {liveCount > 0 && <span className="text-ink-400">{liveCount}</span>}
      </h2>

      <div className="rounded-2xl bg-surface p-5 shadow-card">
        {loading ? (
          <div className="py-6 text-center">
            <Spinner />
          </div>
        ) : roots.length === 0 ? (
          <p className="py-4 text-center text-[13px] text-ink-300">첫 댓글을 남겨보세요.</p>
        ) : (
          <div className="space-y-5">
            {roots.map((root) => (
              <div key={root.id} className="space-y-4">
                <CommentItem
                  comment={root}
                  isReply={false}
                  onReply={setReplyTo}
                  onDelete={(id) => void remove(id)}
                  onReactionChange={updateReactions}
                />
                {repliesOf(root.id).map((reply) => (
                  <CommentItem
                    key={reply.id}
                    comment={reply}
                    isReply
                    onReply={setReplyTo}
                    onDelete={(id) => void remove(id)}
                    onReactionChange={updateReactions}
                  />
                ))}
              </div>
            ))}
          </div>
        )}

        <div className="mt-5 border-t border-ink-100 pt-4">
          {replyTo && (
            <div className="mb-2 flex items-center justify-between text-[12px] text-ink-500">
              <span>답글을 다는 중</span>
              <button onClick={() => setReplyTo(null)} className="hover:text-ink-900">
                취소
              </button>
            </div>
          )}
          <TextArea
            rows={3}
            value={body}
            maxLength={COMMENT_LIMITS.bodyMax}
            disabled={busy}
            placeholder={replyTo ? '답글을 남겨주세요' : '댓글을 남겨주세요'}
            onChange={(e) => setBody(e.target.value)}
          />
          <div className="mt-2 flex items-center justify-between gap-2">
            {isAdmin ? (
              <label className="flex items-center gap-2 text-[13px] text-ink-600">
                <input
                  type="checkbox"
                  checked={asAdmin}
                  disabled={busy}
                  onChange={(e) => setAsAdmin(e.target.checked)}
                  className="h-4 w-4 rounded border-ink-300 text-tomato-600 focus:ring-tomato-500"
                />
                <span>
                  <b className="font-medium">농장 관리인</b> 명의
                </span>
              </label>
            ) : (
              <span />
            )}
            <Button size="sm" disabled={!body.trim() || busy} onClick={() => void submit()}>
              {busy ? '남기는 중…' : '남기기'}
            </Button>
          </div>
          <Notice>{error}</Notice>
        </div>
      </div>
    </section>
  );
}
